import * as Sentry from "@sentry/react";
import React, { useMemo } from "react";

const sendErrorIfPossible = (error?: Error) => {
  if (error) {
    return Sentry.captureException(error);
  }
  return null;
};
/**
 * Send error to error monitor if possible
 */
export const useErrorLogger = (error?: Error) => useMemo(() => sendErrorIfPossible(error), [error]);

/**
 * Custom hook for managing and handling errors within a React component.
 * The `useErrorHandler` hook provides a mechanism for handling errors in a predictable manner. It allows you to catch and record errors, optionally report them through an external mechanism, and provides utilities to clear the stored errors and reset the state.
 *
 */
export const useErrorHandler = () => {
  const [errors, setErrors] = React.useState<Error[]>([]);
  const [errorId, setErrorId] = React.useState<string | null>(null);
  const catchErr = (callback: () => void, catchCallback?: (error: any) => void) => {
    try {
      callback();
    } catch (error) {
      setErrors((prev) => [...prev, error as Error]);
      setErrorId(sendErrorIfPossible(error as Error));
      catchCallback?.(error);
    }
  };
  const clearErr = () => {
    setErrors([]);
    setErrorId(null);
  };
  return { errors, errorId, catchErr, clearErr };
};
